/**
 * profileSyncState — Global sync progress for profile propagation.
 *
 * Tracks completed/total steps so a single UI indicator can show progress.
 */

export interface SyncProgress {
  isSyncing: boolean;
  completed: number;
  total: number;
  error: boolean;
}

type SyncListener = (progress: SyncProgress) => void;

let _state: SyncProgress = { isSyncing: false, completed: 0, total: 0, error: false };
const _listeners = new Set<SyncListener>();

function notify() {
  const snapshot = { ..._state };
  _listeners.forEach((fn) => {
    try { fn(snapshot); } catch (e) { console.error("[profileSyncState] listener error", e); }
  });
}

export function getSyncProgress(): SyncProgress {
  return { ..._state };
}

/** Legacy compat — returns true while syncing */
export function isProfileSyncing(): boolean {
  return _state.isSyncing;
}

export function startSync(total: number): void {
  _state = { isSyncing: true, completed: 0, total, error: false };
  console.debug("[profileSyncState] start", total, "steps");
  notify();
}

export function tickSync(): void {
  if (!_state.isSyncing) return;
  _state = { ..._state, completed: _state.completed + 1 };
  notify();
}

export function endSync(error = false): void {
  _state = { isSyncing: false, completed: _state.total, total: _state.total, error };
  console.debug("[profileSyncState]", error ? "error ✗" : "synced ✓");
  notify();
}

/** Legacy compat */
export function setProfileSyncing(value: boolean): void {
  if (value) startSync(0);
  else endSync();
}

/** Subscribe to sync state changes. Returns unsubscribe. */
export function onSyncChange(listener: SyncListener): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}
